// Third Party
import * as React from 'react';
import { Dialog, DialogBackdrop, DialogTitle } from '@headlessui/react';
import Image from 'next/image';

// Utils
import { imagekitLoader } from '@utils/imagekit-loader';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { useAsyncSafeState, useMounted } from '@hooks';
import { ListingBySlugPartial } from '@utils/imperative-node-queries';
import { routes } from '@routes';

// UI
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterIcon,
  TwitterShareButton,
  EmailShareButton,
  EmailIcon,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { Icon, IconNames } from '@components/icon';
import { ButtonIcon } from '@components/button-icon';
import { concatenateAndReplace } from '@utils/test-helpers';

type Props = {
  isOpen: boolean;
  closeModal: VoidFunction;
  listing: ListingBySlugPartial;
};

export const ShareModal: React.FC<Props> = ({
  isOpen,
  closeModal,
  listing,
}) => {
  const mounted = useMounted();
  const focusDiv = React.useRef(null);

  const [showCopySuccess, setShowCopySuccess] =
    useAsyncSafeState<boolean>(false);

  const pageUrl = new URL(
    routes.listings({
      slug: listing.slug,
    }),
    'https://www.savvy.com'
  ).toString();

  const copyUrl = () => {
    void navigator.clipboard.writeText(pageUrl).then(() => {
      setShowCopySuccess(true);
      setTimeout(() => {
        setShowCopySuccess(false);
      }, 500);
    });
  };

  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modalActive');
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    }
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <Dialog
      initialFocus={focusDiv}
      as="div"
      className="fixed inset-0 overflow-y-auto z-50"
      onClose={closeModal}
      open={isOpen}
    >
      <div ref={focusDiv} className="min-h-screen px-4 text-center">
        <DialogBackdrop className="fixed inset-0 bg-black opacity-60" />
        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-xl px-4 pt-4 pb-12 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          <div className="flex flex-row justify-end">
            <ButtonIcon
              onClick={closeModal}
              variant="text"
              icon={IconNames.X}
              ariaDescribedby="Close share modal"
            />
          </div>

          <DialogTitle as="h2" className="text-2xl text-dark text-center">
            Share this B&#38;B with friends and family
          </DialogTitle>
          <div className="px-6 flex flex-col w-full mt-4 space-y-4">
            <h3
              className="text-xl font-bold text-dark text-center"
              data-testid={concatenateAndReplace(`${listing.name}`)}
            >
              {listing.name}
            </h3>
            {listing.galleryAssets.length > 0 && (
              <div className="w-60 h-60 mx-auto relative">
                <Image
                  layout="fill"
                  className="object-cover object-center rounded-lg"
                  src={listing.galleryAssets[0].url || ''}
                  alt={listing.galleryAssets[0].description || 'listing image'}
                  placeholder="blur"
                  loader={imagekitLoader}
                  blurDataURL={GRAY_PIXEL_DATA_URL}
                  sizes="60rem"
                />
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-row justify-center space-x-2 sm:space-x-4">
            <FacebookShareButton
              style={{ outline: 'none' }}
              url={pageUrl}
              hashtag="Savvy"
            >
              <FacebookIcon className="w-12 h-12 sm:w-16 sm:h-16 rounded-md" />
            </FacebookShareButton>

            <TwitterShareButton
              style={{ outline: 'none' }}
              url={pageUrl}
              title={'test'}
              hashtags={['Savvy']}
              related={['Savvy']}
            >
              <TwitterIcon className="w-12 h-12 sm:w-16 sm:h-16 rounded-md" />
            </TwitterShareButton>

            <EmailShareButton
              style={{ outline: 'none' }}
              url={pageUrl}
              subject={'test'}
              body={`Check out this vacation rental on Savvy! ${pageUrl}`}
            >
              <EmailIcon className="w-12 h-12 sm:w-16 sm:h-16 rounded-md" />
            </EmailShareButton>

            <a
              href={`sms:&body=Check out this vacation rental on Savvy! ${pageUrl}`}
            >
              <svg
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                role="presentation"
                focusable="false"
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-md"
              >
                <defs>
                  <linearGradient
                    x1="50.794%"
                    y1="93.362%"
                    x2="50.794%"
                    y2="12.41%"
                    id="imessagegradient"
                  >
                    <stop stopColor="#0CBD2A" offset="0%"></stop>
                    <stop stopColor="#5BF675" offset="100%"></stop>
                  </linearGradient>
                </defs>
                <g>
                  <path
                    d="M2 0h28a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z"
                    fill="url(#imessagegradient)"
                  ></path>
                  <path
                    d="M15.796 5.469c-6.404 0-11.595 4.324-11.595 9.658.005 3.39 2.143 6.528 5.633 8.27-.457 1.023-1.142 1.983-2.028 2.838 1.717-.3 3.329-.934 4.71-1.85 1.064.264 2.17.399 3.28.4 6.404 0 11.596-4.324 11.596-9.658S22.2 5.47 15.796 5.47z"
                    fill="#FFF"
                  ></path>
                </g>
              </svg>
            </a>

            <WhatsappShareButton
              style={{ outline: 'none' }}
              url={pageUrl}
              title={'test'}
            >
              <WhatsappIcon className="w-12 h-12 sm:w-16 sm:h-16 rounded-md" />
            </WhatsappShareButton>
          </div>

          <div className="flex flex-col justify-center mt-4 mx-auto sm:flex-row sm:w-full relative">
            <input
              value={pageUrl}
              placeholder="Enter an email"
              type="text"
              name="copy-url"
              alt="copy url"
              className="p-4 border-2 border-primary-main rounded-md focus:ring-1 sm:w-72 sm:text-sm sm:rounded-l-md sm:rounded-none"
            />

            <button
              onClick={copyUrl}
              type="submit"
              value="Submit"
              name="subscribe"
              style={{ outline: 'none' }}
              className="outline-none focus:outline-none mt-2 px-8 py-4 text-white text-sm font-bold hover:bg-primary-light bg-primary-main border border-primary-main rounded-md focus:ring-0 sm:-ml-px sm:mt-0 sm:py-2 sm:rounded-none sm:rounded-r-md"
            >
              Copy
            </button>
          </div>
          {showCopySuccess && (
            <div className="absolute bottom-0 flex flex-row justify-center w-full mb-4">
              <div className="bg-white p-4 shadow-lg rounded-xl border-2 border-success-main flex flex-row items-center font-semibold">
                <Icon
                  name={IconNames.CIRCLE_CHECK}
                  size={'sm'}
                  className={'text-success-main'}
                />
                Link Copied
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
